import { create } from '@fhs/zustand';
import { CartPaymentCardType } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { IUserAccountsQuery } from '@fhs-legacy/frontend/src/generated/rbi-graphql';

import { CardData, PaymentMethodType } from '../../payment';
import { IEncryptionService, ISavedCardInfo } from '../../payment/payment-types';
import {
  detectCardBrand,
  mapCardBrandToCartPaymentCardType,
} from '../../payment/services/detect-card-brand-utils';

export interface PaymentMethodStore {
  showAddPaymentMethod: boolean;
  newCardData: CardData | null;
  creditType: CartPaymentCardType | null;
  selectedPaymentMethod: PaymentMethodType;
  userAccountsData: IUserAccountsQuery;
  savedCardData: ISavedCardInfo | undefined;
  encryptionService: IEncryptionService | null;
  failedVaulting: boolean;
  setSelectedPaymentMethod: (method: PaymentMethodType | null) => void;
  setShowAddPaymentMethod: (show: boolean) => void;
  setUserAccountsData: (res: IUserAccountsQuery) => void;
  setSelectedSavedCard: (cardInfo: ISavedCardInfo) => void;
  setNewCardData: (data: CardData | null) => void;
  setCreditType: (cardNumber: string | CartPaymentCardType) => void;
  setEncryptionService: (service: IEncryptionService | null) => void;
  setFailedVaulting: (isFailed: boolean) => void;
  isPaymentReady: () => boolean;
  removeNewCardData: () => void;
  resetPaymentMethodStore: () => void;
}

const initialState = {
  showAddPaymentMethod: false,
  selectedPaymentMethod: null,
  userAccountsData: {},
  newCardData: null,
  savedCardData: undefined,
  encryptionService: null,
  failedVaulting: false,
  creditType: null,
};

export const usePaymentMethodStore = create<PaymentMethodStore>((set, get) => ({
  ...initialState,
  setSelectedPaymentMethod: id => set({ selectedPaymentMethod: id }),
  setShowAddPaymentMethod: show => set({ showAddPaymentMethod: show }),
  setUserAccountsData: res => {
    const savedCardAccount = res?.userAccounts?.accounts?.find(
      account => account?.credit?.__typename === 'CreditCard'
    );

    // Merge account-level identifier with credit card details
    // Both Orbital and Worldpay use accountIdentifier
    const defaultSavedCard = savedCardAccount?.credit
      ? ({
          ...savedCardAccount.credit,
          accountIdentifier:
            savedCardAccount.accountIdentifier || (savedCardAccount as any).fdAccountId,
        } as ISavedCardInfo)
      : undefined;

    // Map saved card's cardType to CartPaymentCardType for pipeline context
    const creditType = defaultSavedCard
      ? mapCardBrandToCartPaymentCardType(defaultSavedCard.cardType)
      : null;

    set({
      userAccountsData: res,
      savedCardData: defaultSavedCard,
      creditType,
      selectedPaymentMethod: defaultSavedCard ? 'savedCard' : null,
      showAddPaymentMethod: !defaultSavedCard,
    });
  },
  setSelectedSavedCard: cardInfo => {
    const creditType = mapCardBrandToCartPaymentCardType(cardInfo.cardType);
    set({
      savedCardData: cardInfo,
      creditType,
      selectedPaymentMethod: 'savedCard',
      showAddPaymentMethod: false,
    });
  },
  setNewCardData: data => set({ newCardData: data }),
  setCreditType: cardNumberOrType => {
    if (typeof cardNumberOrType === 'string') {
      set({ creditType: detectCardBrand(cardNumberOrType) });
    } else {
      set({ creditType: cardNumberOrType });
    }
  },
  setEncryptionService: service => set({ encryptionService: service }),
  removeNewCardData: () => set({ newCardData: null }),
  resetPaymentMethodStore: () =>
    set({
      ...initialState,
    }),
  setFailedVaulting: (isFailed: boolean) => set({ failedVaulting: isFailed }),
  isPaymentReady: (): boolean => {
    const { selectedPaymentMethod, newCardData } = get();

    switch (selectedPaymentMethod) {
      // new card flow
      case null:
        return !!(
          newCardData?.cardNumber &&
          newCardData?.cvv &&
          newCardData?.expiryMonth &&
          newCardData?.expiryYear &&
          newCardData?.postalCode
        );

      case 'savedCard':
      case 'applePay':
      case 'googlePay':
      case 'giftCard':
        // Digital wallets and saved cards are ready when selected
        // The pipeline will handle requesting payment details
        return true;

      default:
        return false;
    }
  },
}));
